import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useCart } from '../context/CartContext';
import api, { orderAPI, contentAPI } from '../services/api';
import { OrderFormData, DeliveryOption, CartItem } from '../types';
import './Checkout.css';
import { User, Phone, MapPin, MessageSquare, ShieldCheck, Truck, ShoppingBag, ArrowRight, Check } from 'lucide-react';

import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';

const Checkout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, clearCart, updateQuantity, removeItem } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [directOrderLoaded, setDirectOrderLoaded] = useState(false);
  const [directOrderItems, setDirectOrderItems] = useState<CartItem[]>([]);
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<number | null>(null);
  const [couponCode,setCouponCode]=useState('');
  const [couponApplied, setCouponApplied] = useState<{ code: string; discount_amount: number } | null>(null);
  const [couponError, setCouponError] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);

  const [formData, setFormData] = useState<OrderFormData>({
    customer_name: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
  });

  // PreOrder state for auto-save
  const [preorderId, setPreorderId] = useState<number | null>(null);
  const [, setIsSavingPreorder] = useState(false);
  const preorderSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle direct order data from sessionStorage — uses local state, NOT CartContext
  // This ensures the cart badge is never affected by clicking "অর্ডার করুন"
  useEffect(() => {
    const directOrderData = sessionStorage.getItem('directOrder');
    if (directOrderData && !directOrderLoaded) {
      try {
        const orderData = JSON.parse(directOrderData);

        // Use the product data directly from sessionStorage (already fetched)
        if (orderData.product) {
          const quantity = parseInt(orderData.quantity, 10) || 1;
          setDirectOrderItems([{
            product: orderData.product,
            quantity,
            selected_color: orderData.selected_color || '',
            selected_size: orderData.selected_size || '',
            selected_weight: orderData.selected_weight || '',
            selected_variant_id: orderData.selected_variant_id,
            selected_variant_sku: orderData.selected_variant_sku || '',
          }]);
          sessionStorage.removeItem('directOrder');
          setDirectOrderLoaded(true);
        }
      } catch (err) {
        console.error('Error handling direct order:', err);
        setDirectOrderLoaded(true);
      }
    }
  }, [directOrderLoaded]);

  // Computed: use direct order items if present, otherwise use cart items
  const isDirectOrder = directOrderItems.length > 0;
  const activeItems = isDirectOrder ? directOrderItems : items;

  // Fetch delivery options
  useEffect(() => {
    const fetchDeliveryOptions = async () => {
      try {
        const response = await contentAPI.getDeliveryOptions();
        // Handle paginated response (DRF returns {results: []} for viewsets)
        const data = Array.isArray(response) ? response : (response.results || []);
        setDeliveryOptions(data);
        // Auto-select first option
        if (data.length > 0) {
          setSelectedDeliveryOption(data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch delivery options:', err);
        setDeliveryOptions([]); // Ensure it's always an array
      }
    };
    fetchDeliveryOptions();
  }, []);

  const selectedOption = Array.isArray(deliveryOptions) ? deliveryOptions.find(opt => opt.id === selectedDeliveryOption) : null;
  const shippingCost = selectedOption ? Number(selectedOption.cost) : 0;
  const getItemPrice = (item: CartItem) => {
    const product = item.product as any;
    return Number(product.website_price || product.final_price || product.discount_price || product.price || 0);
  };
  const activeTotal = activeItems.reduce((sum, item) => {
    const price = getItemPrice(item);
    return sum + price * item.quantity;
  }, 0);
  const couponDiscount = couponApplied ? Math.min(couponApplied.discount_amount, activeTotal) : 0;
  const finalTotal = activeTotal + shippingCost - couponDiscount;

  useEffect(() => {
    setCouponApplied(null);
    setCouponError('');
  }, [activeTotal]);

  // Track InitiateCheckout
  useEffect(() => {
    if (activeItems.length > 0) {
      trackFacebookEvent('InitiateCheckout', {
        content_ids: activeItems.map(item => item.product.id.toString()),
        content_type: 'product',
        value: activeTotal,
        currency: 'BDT',
        num_items: activeItems.reduce((acc, item) => acc + item.quantity, 0)
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItems.length]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Auto-save preorder with debounce (500ms after user stops typing)
  const savePreorder = useCallback(async () => {
    // Only save if we have items and at least some form data
    if (activeItems.length === 0) return;
    if (!formData.customer_name && !formData.customer_phone && !formData.shipping_address) return;

    setIsSavingPreorder(true);
    try {
      const preorderData = {
        customer_name: formData.customer_name || '',
        customer_phone: formData.customer_phone || '',
        shipping_address: formData.shipping_address || '',
        notes: formData.notes || '',
        delivery_option: selectedDeliveryOption,
        shipping_cost: shippingCost,
        items: activeItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          selected_color: item.selected_color || '',
          selected_size: item.selected_size || '',
          selected_weight: item.selected_weight || '',
          product_variant_id: item.selected_variant_id,
        })),
      };

      if (preorderId) {
        // Update existing preorder
        await orderAPI.updatePreOrder(preorderId, preorderData);
      } else {
        // Create new preorder
        const newPreorder = await orderAPI.createPreOrder(preorderData);
        if (newPreorder.id) {
          setPreorderId(newPreorder.id);
        }
      }
    } catch (err) {
      console.error('Auto-save preorder failed:', err);
      // Silently fail - don't interrupt user
    } finally {
      setIsSavingPreorder(false);
    }
  }, [formData, activeItems, selectedDeliveryOption, shippingCost, preorderId]);

  // Debounced auto-save effect
  useEffect(() => {
    // Clear previous timeout
    if (preorderSaveTimeoutRef.current) {
      clearTimeout(preorderSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (500ms delay)
    preorderSaveTimeoutRef.current = setTimeout(() => {
      savePreorder();
    }, 500);

    // Cleanup on unmount
    return () => {
      if (preorderSaveTimeoutRef.current) {
        clearTimeout(preorderSaveTimeoutRef.current);
      }
    };
  }, [formData, items, selectedDeliveryOption, savePreorder]);

  const validateForm = (): boolean => {
    if (!formData.customer_name.trim()) {
      setError(t('checkout.errors.nameRequired'));
      return false;
    }
    if (!formData.customer_phone.trim() || formData.customer_phone.length < 11) {
      setError(t('checkout.errors.phoneInvalid'));
      return false;
    }
    if (!formData.shipping_address.trim()) {
      setError(t('checkout.errors.addressRequired'));
      return false;
    }
    if (!selectedDeliveryOption) {
      setError(t('checkout.selectDelivery') || 'অনুগ্রহ করে ডেলিভারি অপশন নির্বাচন করুন');
      return false;
    }
    return true;
  };

  const handleApplyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    setCouponError('');
    setCouponApplied(null);
    if (!code) {
      setCouponError('Enter a coupon code.');
      return;
    }
    if (activeTotal <= 0) {
      setCouponError('Add products before applying a coupon.');
      return;
    }
    setCouponChecking(true);
    try {
      const response = await api.post('/coupons/validate_code/', {
        code,
        channel: 'WEBSITE',
        purchase_amount: activeTotal,
        line_items: activeItems.map(item => ({
          product_id: item.product.id,
          subtotal: getItemPrice(item) * item.quantity,
        })),
      });
      const data = response.data;
      if (!data.valid || Number(data.discount_amount || 0) <= 0) {
        setCouponError(data.error || 'This coupon is not applicable to this order.');
        return;
      }
      setCouponApplied({ code, discount_amount: Number(data.discount_amount || 0) });
      setCouponCode(code);
    } catch (err: any) {
      setCouponError(err.response?.data?.error || 'Could not validate coupon.');
    } finally {
      setCouponChecking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    if (activeItems.length === 0) {
      setError(t('checkout.errors.emptyCart'));
      return;
    }

    setLoading(true);

    try {
      // Track Facebook AddPaymentInfo
      await trackFacebookEvent('AddPaymentInfo', {
        content_ids: activeItems.map(item => item.product.id.toString()),
        content_type: 'product',
        value: finalTotal,
        currency: 'BDT',
        payment_type: 'Cash On Delivery'
      });

      // Track GTM/GA4 add_payment_info
      gtmEvents.addPaymentInfo(
        activeItems.map(item => ({
          id: item.product.id,
          name: item.product.name_en,
          price: getItemPrice(item),
          quantity: item.quantity,
        })),
        finalTotal,
        'Cash On Delivery'
      );

      const orderData = {
        ...formData,
        coupon_code: couponApplied?.code || couponCode.trim().toUpperCase(),
        delivery_option: selectedDeliveryOption,
        shipping_cost: shippingCost,
        items: activeItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          selected_color: item.selected_color || '',
          selected_size: item.selected_size || '',
          selected_weight: item.selected_weight || '',
          product_variant_id: item.selected_variant_id,
        })),
      };

      let order;
      if (preorderId) {
        // Confirm existing preorder
        order = await orderAPI.confirmPreOrder(preorderId, {coupon_code: couponApplied?.code || couponCode.trim().toUpperCase()});
      } else {
        // Create new order directly (fallback if preorder wasn't created)
        order = await orderAPI.createOrder(orderData);
      }
      // Only clear the shared cart on a regular cart checkout.
      // For direct orders, the cart is untouched.
      if (!isDirectOrder) {
        clearCart();
      }
      navigate(`/order-confirmation/${order.order_number}`);
    } catch (err: any) {
      console.error('Order creation failed:', err);

      let errorMessage = t('checkout.errors.orderFailed');

      if (err.response?.data) {
        const errorData = err.response.data;

        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors.join(', ')
            : errorData.non_field_errors;
        } else {
          const fieldErrors = Object.entries(errorData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (activeItems.length === 0) {
    return (
      <div className="checkout-page empty-state">
        <div className="container">
          <div className="empty-cart-content">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
            </div>
            <h2>{t('cart.empty')}</h2>
            <p>{t('cart.emptyMessage') || 'Your count appears to be empty. Start shopping now!'}</p>
            <Link to="/products" className="btn btn-primary start-shopping-btn">
              {t('cart.continueShopping')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        {/* Stepper */}
        <div className="checkout-steps">
          <div className="step completed">
            <div className="step-icon">
              <Check size={16} />
            </div>
            <span>{t('checkout.steps.cart') || 'Cart'}</span>
          </div>
          <div className="step-line active"></div>
          <div className="step active">
            <div className="step-icon">2</div>
            <span>{t('checkout.steps.checkout') || 'Checkout'}</span>
          </div>
          <div className="step-line"></div>
          <div className="step">
            <div className="step-icon">3</div>
            <span>{t('checkout.steps.confirmation') || 'Confirmation'}</span>
          </div>
        </div>

        <div className="checkout-layout">

          {/* Left Column: Form */}
          <div className="checkout-form-column">
            <div className="checkout-card form-wrapper">

              <form id="checkout-form" onSubmit={handleSubmit}>
                {error && (
                  <div className="error-alert">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                <div className="form-section customer-info-section">
                  <div className="form-section-header">
                    <h3 className="section-title">
                      <span className="step-number">1</span>
                      {t('checkout.customerInfo')}
                    </h3>
                  </div>

                  <div className="customer-fields">
                    <div className="customer-form-grid">
                      <div className="customer-form-row">
                        <div className="form-group">
                          <label htmlFor="customer_name">
                            {t('checkout.name')} <span className="required">*</span>
                          </label>
                          <div className="checkout-input-wrapper">
                            <User size={18} className="input-icon" />
                            <input
                              type="text"
                              id="customer_name"
                              name="customer_name"
                              value={formData.customer_name}
                              onChange={handleInputChange}
                              placeholder={t('checkout.namePlaceholder') || "Enter your full name"}
                              required
                              className="checkout-input"
                              autoComplete="name"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label htmlFor="customer_phone">
                            {t('checkout.phone')} <span className="required">*</span>
                          </label>
                          <div className="checkout-input-wrapper">
                            <Phone size={18} className="input-icon" />
                            <input
                              type="tel"
                              id="customer_phone"
                              name="customer_phone"
                              value={formData.customer_phone}
                              onChange={handleInputChange}
                              placeholder="01XXXXXXXXX"
                              required
                              className="checkout-input"
                              autoComplete="tel"
                              inputMode="numeric"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="shipping_address">
                          {t('checkout.address')} <span className="required">*</span>
                        </label>
                        <div className="checkout-input-wrapper textarea-wrapper">
                          <MapPin size={18} className="input-icon" />
                          <textarea
                            id="shipping_address"
                            name="shipping_address"
                            value={formData.shipping_address}
                            onChange={handleInputChange}
                            rows={3}
                            placeholder={t('checkout.addressPlaceholder') || "Enter your complete delivery address"}
                            required
                            className="checkout-input"
                            autoComplete="street-address"
                          />
                        </div>
                      </div>

                      {/* Payment Method */}
                      <div className="form-group full-width" style={{ marginBottom: '8px' }}>
                        <label className="delivery-section-label" style={{ marginTop: '8px' }}>
                          <span className="delivery-icon">💳</span>
                          {t('checkout.paymentMethod') || 'Payment Method'}
                        </label>
                        <div className="payment-method-box">
                          <div className="payment-header">
                            <div className="radio-circle selected"></div>
                            <span className="payment-title">{t('checkout.cashOnDelivery')}</span>
                          </div>
                          <p className="payment-desc">{t('checkout.paymentDesc')}</p>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="order_notes">{t('checkout.orderNotes')}</label>
                        <div className="checkout-input-wrapper textarea-wrapper">
                          <MessageSquare size={18} className="input-icon" />
                          <textarea
                            id="order_notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            rows={2}
                            placeholder={t('checkout.notesPlaceholder') || "Any special instructions for delivery (optional)"}
                            className="checkout-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column: Order Summary */}
          <div className="checkout-summary-column">
            <div className="checkout-card summary-card sticky-card">
              <h3>{t('cart.orderSummary')}</h3>

              <div className="summary-products">
                {activeItems.map((item) => (
                  <div key={item.product.id} className="summary-product-item">
                    <div className="product-thumb">
                      {item.product.image || item.product.primary_image ? (
                        <img
                          src={item.product.image || item.product.primary_image}
                          alt={item.product.name}
                        />
                      ) : (
                        <div className="placeholder-thumb">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        </div>
                      )}
                      <span className="product-qty-badge">{item.quantity}</span>
                    </div>
                    <div className="product-info">
                      <span className="product-name">{item.product.name}</span>
                      {(item.selected_color || item.selected_size || item.selected_weight) && (
                        <div className="checkout-variant-list">
                          {item.selected_color && (
                            <span className="checkout-variant-badge color">
                              <span className="color-dot" style={{ backgroundColor: item.selected_color }}></span>
                              {item.selected_color}
                            </span>
                          )}
                          {item.selected_size && (
                            <span className="checkout-variant-badge size">
                              {item.selected_size}
                            </span>
                          )}
                          {item.selected_weight && (
                            <span className="checkout-variant-badge weight">
                              {item.selected_weight}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="product-total" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
                      <span className="total-price" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-main)' }}>
                        ৳{(getItemPrice(item) * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="summary-breakdown">
                <div className="checkout-coupon-wrapper">
                  <input
                    value={couponCode}
                    onChange={e => { setCouponCode(e.target.value); setCouponApplied(null); setCouponError(''); }}
                    placeholder={t('checkout.couponPlaceholder') || "Coupon code"}
                    className="checkout-coupon-input"
                  />
                  <button
                    type="button"
                    onClick={handleApplyCoupon}
                    disabled={couponChecking || !couponCode.trim()}
                    className="checkout-coupon-btn"
                  >
                    {couponChecking ? '...' : (t('checkout.applyCoupon') || 'Apply')}
                  </button>
                </div>
                {couponApplied && (
                  <div className="breakdown-row" style={{ color: '#047857' }}>
                    <span>Coupon ({couponApplied.code})</span>
                    <span>-৳{couponDiscount.toFixed(2)}</span>
                  </div>
                )}
                {couponError && <div style={{ color: '#b91c1c', fontSize: '0.82rem', marginTop: '-4px' }}>{couponError}</div>}
                <div className="breakdown-row">
                  <span>{t('cart.subtotal')}</span>
                  <span>৳{activeTotal.toFixed(2)}</span>
                </div>
                <div className="breakdown-row shipping-row">
                  <span>{t('cart.shipping')}</span>
                  <span>৳{shippingCost.toFixed(2)}</span>
                </div>
                <div className="breakdown-divider"></div>
                <div className="breakdown-row total-row" style={{ marginBottom: '0' }}>
                  <span>{t('cart.total')}</span>
                  <span className="total-amount">৳{finalTotal.toFixed(2)}</span>
                </div>

                <button
                  type="submit"
                  form="checkout-form"
                  className="place-order-btn btn-primary"
                  disabled={loading}
                  style={{ marginTop: '10px' }}
                >
                  {loading ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <>
                      <span>{t('checkout.placeOrder')}</span>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </>
                  )}
                </button>
              </div>

              <div className="security-badges">
                <div className="badge-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <span>{t('checkout.secureCheckout')}</span>
                </div>
                <div className="badge-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>{t('checkout.trackedDelivery')}</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
